import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Plus, UserX } from "lucide-react";

import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import {
  UIButton,
  UIEmptyState,
  UIPaginationBar,
} from "../../../components/ui";
import ClientsTable from "./ClientsTable";
import TeamToolbar from "../TeamsPermissions/TeamToolbar";

const ClientsListView = ({
  breadcrumbLabel,
  title,
  totalClients,
  canAdd,
  addClientLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onAddClient,
  rows,
  page,
  pageSize,
  paginationPages,
  paginationFrom,
  paginationTo,
  showingRangeTemplate,
  rowsPerPageLabel,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  emptyTitle,
  emptyDescription,
  noDataDescription,
  isSearchEmpty,
  isLoading = false,
  tableLabels,
  embedded = false,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return null;
    }

    if (!rows.length) {
      return (
        <UIEmptyState
          classNames={{ root: "company-clients__empty" }}
          title={isSearchEmpty ? noDataDescription : emptyTitle}
          description={isSearchEmpty ? undefined : emptyDescription}
          icon={<UserX aria-hidden />}
          actionLabel={isSearchEmpty || !canAdd ? undefined : addClientLabel}
          actionIcon={isSearchEmpty || !canAdd ? undefined : <Plus aria-hidden />}
          onAction={isSearchEmpty || !canAdd ? undefined : onAddClient}
        />
      );
    }

    return (
      <>
        <ClientsTable
          rows={rows}
          labels={tableLabels}
          pageSize={pageSize}
          onRowClick={onRowClick}
        />
        {totalClients > 0 ? (
          <UIPaginationBar
            currentPage={page}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={showingRangeTemplate}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={totalClients}
            rowsPerPageLabel={rowsPerPageLabel}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            className="company-clients__pagination"
          />
        ) : null}
      </>
    );
  };

  return (
    <div className={embedded ? "company-clients company-clients--embedded" : "company-clients"}>
      {!embedded ? (
        <div className="page-title-margin">
          <Breadcrumbs
            list={[{ path: "", label: breadcrumbLabel }]}
            BreadCrumbHasBackIcon={false}
          />
          <Box className="company-clients__header">
            <Box className="company-clients__title-row">
              <Typography variant="h5">{title}</Typography>
              <span className="company-clients__counter">{totalClients}</span>
            </Box>
            {canAdd ? (
              <UIButton
                variant="primary"
                appearance="outline"
                size="large"
                type="button"
                className="company-clients__add-btn"
                startIcon={<Plus aria-hidden size={20} />}
                onClick={onAddClient}
              >
                {addClientLabel}
              </UIButton>
            ) : null}
          </Box>
        </div>
      ) : null}

      {embedded ? (
        <TeamToolbar
          search={searchValue}
          searchPlaceholder={searchPlaceholder}
          onSearchChange={(event) => onSearchChange(event.target.value)}
        >
          {canAdd ? (
            <UIButton
              variant="primary"
              appearance="outline"
              size="large"
              type="button"
              className="team-management__add company-clients__add-btn"
              startIcon={<Plus aria-hidden size={20} />}
              onClick={onAddClient}
            >
              {addClientLabel}
            </UIButton>
          ) : null}
        </TeamToolbar>
      ) : (
        <Filters
          searchInput={{
            placeholder: searchPlaceholder,
            name: "search",
            value: searchValue,
            onChange: onSearchChange,
          }}
          filters={[]}
          classNames={{
            container: "company-clients__filters",
            base: "",
            search: "company-clients__search mb-3 mb-md-0",
            filter: "",
          }}
        />
      )}

      <div className="company-clients__content">{renderContent()}</div>
    </div>
  );
};

ClientsListView.propTypes = {
  breadcrumbLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  totalClients: PropTypes.number.isRequired,
  canAdd: PropTypes.bool.isRequired,
  addClientLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string.isRequired,
  searchValue: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onAddClient: PropTypes.func,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  paginationPages: PropTypes.number.isRequired,
  paginationFrom: PropTypes.number.isRequired,
  paginationTo: PropTypes.number.isRequired,
  showingRangeTemplate: PropTypes.string.isRequired,
  rowsPerPageLabel: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  noDataDescription: PropTypes.string.isRequired,
  isSearchEmpty: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  tableLabels: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    mobile: PropTypes.string.isRequired,
  }).isRequired,
  embedded: PropTypes.bool,
};

export default ClientsListView;
